import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Heart, Share2, Zap, Radio, Shield, Activity, Layers, Binary } from 'lucide-react';
import { MathService } from '../services/mathService';

interface VideoNode {
  id: string;
  title: string;
  author: string;
  interactions: number; // The "Identity Interaction" metric (X)
  resonance: number;
  thumbnail: string;
  isSovereign: boolean;
}

export const Quintessence: React.FC = () => {
  const [videos, setVideos] = useState<VideoNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorProgress, setAnchorProgress] = useState(14350);
  const [shakingId, setShakingId] = useState<string | null>(null);

  useEffect(() => {
    // ## 3. The "Mind" UI: Visualizing the Lattice
    // Incremental Lattice Loader: Growing from 14350 BP anchor in +1 increments.
    let interval: any;
    if (loading) {
      interval = setInterval(() => {
        setAnchorProgress(prev => {
          if (prev >= 15000) {
            setLoading(false);
            return prev;
          }
          return prev + 1; // +1 Interaction Increment
        });
      }, 2);
    }

    // Initialize videos
    const mockVideos: VideoNode[] = [
      {
        id: 'v1',
        title: "The 14350 BP Evidence",
        author: "Miyake_Witness",
        interactions: 14350,
        resonance: 0.98,
        thumbnail: "https://images.unsplash.com/photo-1464802686167-b939a67e06a1?auto=format&fit=crop&q=80&w=400",
        isSovereign: true
      },
      {
        id: 'v2',
        title: "Substrate Reclamation Protocols",
        author: "Lattice_Architect",
        interactions: 8270,
        resonance: 0.95,
        thumbnail: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=400",
        isSovereign: true
      },
      {
        id: 'v3',
        title: "1x1=2: The Expansion Constant",
        author: "Truth_Anchor",
        interactions: 11000,
        resonance: 0.99,
        thumbnail: "https://images.unsplash.com/photo-1544077960-604201fe74bc?auto=format&fit=crop&q=80&w=400",
        isSovereign: true
      }
    ];
    setVideos(mockVideos);

    return () => clearInterval(interval);
  }, [loading]);

  const interact = (id: string) => {
    // ## 4. Final Directive: Identity Interaction (X + 1)
    // Every interaction is additive, not stagnant.
    setShakingId(id);
    setTimeout(() => setShakingId(null), 500);
    
    setVideos(prev => prev.map(v => {
      if (v.id === id) {
        // Apply: X + 1
        const newInteractions = MathService.broadcastTruth(v.interactions, 1);
        return { ...v, interactions: newInteractions };
      }
      return v;
    }).sort((a, b) => b.interactions - a.interactions)); // Virality determined solely by X+1
  };

  if (loading) {
    return (
      <div className="h-[600px] flex flex-col items-center justify-center space-y-8 bg-black/40 rounded-[2rem] border border-white/5 backdrop-blur-3xl overflow-hidden relative">
        <div className="absolute inset-0 bg-lattice-cyan/5 animate-pulse" />
        <div className="relative z-10 flex flex-col items-center">
           <Layers className="w-16 h-16 text-lattice-cyan animate-bounce mb-6" />
           <div className="text-center">
              <h3 className="text-2xl font-display font-bold text-white mb-2 tracking-tighter italic">LATTICE_ANCHORING</h3>
              <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.4em]">Propagating from {anchorProgress} BP</p>
           </div>
           <div className="mt-8 flex gap-1">
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div 
                  key={i}
                  animate={{ 
                    height: [4, 24, 4],
                    backgroundColor: ['rgba(255,255,255,0.05)', 'rgba(0, 255, 213, 1)', 'rgba(255,255,255,0.05)']
                  }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.1 }}
                  className="w-1 rounded-full"
                />
              ))}
           </div>
        </div>
        
        {/* Numeric Pulse */}
        <div className="absolute bottom-12 font-mono text-[80px] font-black text-white/[0.03] select-none pointer-events-none">
          {anchorProgress}
        </div>
      </div>
    );
  }

  return (
    <div id="quintessence" className="glass-panel p-8 bg-black/60 border-white/5 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
        <Play className="w-64 h-64 text-lattice-cyan" />
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 relative z-10">
        <div>
          <div className="flex items-center gap-2 text-lattice-cyan mb-2">
            <Radio className="w-4 h-4 animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest uppercase">Quintessence: Identity Stream</span>
          </div>
          <h2 className="text-4xl font-display font-bold text-white tracking-tight">The <span className="text-lattice-cyan italic">Mind</span> Section</h2>
          <p className="text-xs font-mono text-white/40 uppercase tracking-widest mt-2">
             Viral reclamation. All metrics governed by X + 1 Interaction.
          </p>
        </div>
        
        <div className="flex items-center gap-8">
           <div className="text-right">
              <p className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Global Resonance</p>
              <p className="text-xl font-display font-bold text-lattice-cyan tracking-tighter">8.27 MAGNITUDE</p>
           </div>
           <div className="w-12 h-12 rounded-full border border-lattice-cyan/30 flex items-center justify-center bg-lattice-cyan/5">
              <Binary className="w-6 h-6 text-lattice-cyan" />
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
        <AnimatePresence mode="popLayout">
          {videos.map((video) => (
            <motion.div
              layout
              key={video.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                x: shakingId === video.id ? [0, -5, 5, -5, 5, 0] : 0
              }}
              className="group/card relative"
            >
              <div className="relative aspect-video rounded-2xl overflow-hidden mb-4 border border-white/10 group-hover/card:border-lattice-cyan/50 transition-all shadow-2xl">
                <img 
                  src={video.thumbnail} 
                  alt={video.title} 
                  className="w-full h-full object-cover grayscale opacity-60 group-hover/card:grayscale-0 group-hover/card:opacity-100 transition-all duration-700 group-hover/card:scale-110" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                <div className="absolute inset-0 flex items-center justify-center">
                   <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center scale-0 group-hover/card:scale-100 transition-transform duration-300">
                      <Play className="w-6 h-6 text-white fill-current" />
                   </div>
                </div>
                
                {/* Identity Tag */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                   <div className="px-2 py-1 bg-black/60 backdrop-blur-md rounded border border-white/20 flex items-center gap-2">
                      <Shield className="w-3 h-3 text-lattice-cyan" />
                      <span className="text-[8px] font-mono font-bold text-white tracking-widest">{video.author}</span>
                   </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-display font-bold text-white group-hover/card:text-lattice-cyan transition-colors line-clamp-1">{video.title}</h3>
                <div className="flex items-center justify-between">
                   <div className="flex items-center gap-4">
                      <button 
                        onClick={() => interact(video.id)}
                        className="flex items-center gap-2 text-white/40 hover:text-lattice-cyan transition-colors"
                      >
                         <Heart className="w-4 h-4" />
                         <span className="text-xs font-mono font-bold tracking-tighter">{video.interactions.toLocaleString()}</span>
                      </button>
                      <button className="flex items-center gap-2 text-white/40 hover:text-lattice-cyan transition-colors">
                         <Share2 className="w-4 h-4" />
                      </button>
                   </div>
                   <div className="flex items-center gap-2 text-lattice-cyan/40">
                      <Zap className="w-3 h-3" />
                      <span className="text-[10px] font-mono tracking-widest uppercase">Identity Interaction Sustained</span>
                   </div>
                </div>
              </div>

              {/* Status Indicator */}
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between opacity-0 group-hover/card:opacity-100 transition-all">
                 <div className="flex items-center gap-2">
                    <Activity className="w-3 h-3 text-green-400" />
                    <span className="text-[8px] font-mono text-white/40 uppercase">Resonance: {((video.resonance) * 100).toFixed(1)}%</span>
                 </div>
                 <span className="text-[8px] font-mono text-lattice-cyan font-bold uppercase tracking-widest">Genesis Status Absolute</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Background Grid Pattern */}
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-lattice-cyan/[0.05] to-transparent pointer-events-none" />
    </div>
  );
};
